import React, { useState, useEffect } from 'react';
import { FileText, Download, AlertCircle, CheckCircle, Loader, Calendar } from 'lucide-react';
import { usePageHeader } from '../../contexts/PageHeaderContext';
import p60Service from '../../api/services/p60Service';
import useEmployeeProfile from "../../hooks/useEmployeeProfile";

const EmployeeP60 = ({ hideHeader = false }) => {
  const { setHeader } = usePageHeader();
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingYears, setIsLoadingYears] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [pdfBase64, setPdfBase64] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [fiscalYears, setFiscalYears] = useState([]);
  const [selectedFiscalYear, setSelectedFiscalYear] = useState("");

  // Get current employee data from API
  const {
    employeeNo,
    displayName: employeeName,
    isLoading: isLoadingProfile,
    error: profileError,
  } = useEmployeeProfile();

  // Log for debugging
  useEffect(() => {
    if (employeeNo) {
      console.log(
        "📋 P60 Component - Using Employee No:",
        employeeNo,
        "Name:",
        employeeName,
      );
    }
  }, [employeeNo, employeeName]);

  useEffect(() => {
    if (!hideHeader) {
      setHeader({
        icon: FileText,
        title: "P60 Document",
        subtitle: "Generate and download your P60 end of year tax certificate",
      });
    }

    // Cleanup PDF URL on unmount
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
      if (!hideHeader) {
        setHeader({ icon: null, title: "", subtitle: "" });
      }
    };
  }, [hideHeader, setHeader, pdfUrl]);

  // Fetch closed fiscal years on component mount
  useEffect(() => {
    const fetchFiscalYears = async () => {
      setIsLoadingYears(true);
      setError(null);
      try {
        const years = await p60Service.getClosedFiscalYears();
        console.log("Fiscal years received:", years);
        setFiscalYears(years);

        // Auto-select the most recent fiscal year if available
        if (years && years.length > 0) {
          // Assuming the years have a property like 'name' or 'fiscalYear'
          const latestYear = years[0].name || years[0].fiscalYear || years[0];
          setSelectedFiscalYear(latestYear);
        }
      } catch (err) {
        console.error("Error fetching fiscal years:", err);
        setError("Failed to load fiscal years. Please try again.");
      } finally {
        setIsLoadingYears(false);
      }
    };

    fetchFiscalYears();
  }, []);

  const handleGenerateP60 = async () => {
    if (!employeeNo) {
      setError("Employee number not found. Please ensure you are logged in.");
      return;
    }

    if (!selectedFiscalYear) {
      setError("Please select a fiscal year");
      return;
    }

    console.log(
      "🚀 Generating P60 for employee:",
      employeeNo,
      "fiscal year:",
      selectedFiscalYear,
    );
    setIsGenerating(true);
    setError(null);
    setSuccess(false);
    setPdfBase64(null);

    // Cleanup previous PDF URL
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }

    try {
      // Generate P60 document
      const base64Data = await p60Service.generateP60Document(
        employeeNo,
        selectedFiscalYear,
      );

      setPdfBase64(base64Data);
      setSuccess(true);

      // Create blob URL for PDF preview
      const byteCharacters = atob(base64Data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      setPdfUrl(url);
    } catch (err) {
      console.error("Error generating P60:", err);

      // Show user-friendly error message
      let userFriendlyError = "We're unable to generate your P60 document at the moment. ";

      if (err.response?.status === 404 || err.message?.includes("not found")) {
        userFriendlyError += "No P60 data was found for the selected fiscal year. Please contact HR support.";
      } else if (err.response?.status === 401 || err.response?.status === 403) {
        userFriendlyError += "You don't have permission to generate this document. Please contact HR support.";
      } else if (err.response?.status >= 500) {
        userFriendlyError += "Our system is experiencing technical difficulties. Please try again later or contact HR support.";
      } else {
        userFriendlyError += "Please try again or contact HR support if the problem persists.";
      }

      setError(userFriendlyError);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!pdfBase64) {
      setError("No document available to download");
      return;
    }

    try {
      p60Service.downloadP60Document(pdfBase64, employeeNo, selectedFiscalYear);
    } catch (err) {
      console.error("Error downloading P60:", err);
      setError("Failed to download document. Please try again.");
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Loading State */}
      {isLoadingProfile && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
          <div className="flex items-center gap-3">
            <Loader className="w-5 h-5 text-blue-600 animate-spin" />
            <p className="text-blue-900">Loading employee profile...</p>
          </div>
        </div>
      )}

      {/* Profile Error */}
      {profileError && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="font-semibold text-red-900 mb-1">
                Unable to Load Profile
              </p>
              <p className="text-sm text-red-800">
                Failed to fetch employee information. Please try refreshing the
                page.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header Card */}
      <div className="bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl shadow-lg p-8 mb-6 text-white">
        <div className="flex items-start gap-4">
          <div className="bg-white/20 p-3 rounded-xl backdrop-blur-sm">
            <FileText className="w-8 h-8" />
          </div>
          <div className="flex-1">
            <h1 className="text-3xl font-bold mb-2">P60 Document</h1>
            <p className="text-blue-100 text-lg">
              Generate and download your P60 end of year tax certificate
            </p>
            {employeeName && (
              <p className="text-blue-200 mt-2">
                Employee: {employeeName} ({employeeNo})
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-2">What is a P60?</p>
            <p className="mb-2">
              A P60 is an end of year certificate that shows the total amount of
              pay and tax deducted during the tax year (6th April to 5th April).
            </p>
            <p className="text-blue-800">
              You may need your P60 to claim back overpaid tax, apply for tax
              credits, or as proof of earnings.
            </p>
          </div>
        </div>
      </div>

      {/* Generate Button Card */}
      <div className="bg-white rounded-xl shadow-md p-8 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">
          Generate P60 Document
        </h2>
        <p className="text-gray-600 mb-6">
          Select a fiscal year and click the button below to generate your P60
          document. The document will be created in PDF format and can be viewed
          or downloaded.
        </p>

        {/* Fiscal Year Selector */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <Calendar className="w-4 h-4 inline mr-2" />
            Select Fiscal Year
          </label>
          {isLoadingYears ? (
            <div className="flex items-center gap-2 text-gray-500">
              <Loader className="w-4 h-4 animate-spin" />
              <span>Loading fiscal years...</span>
            </div>
          ) : fiscalYears.length > 0 ? (
            <select
              value={selectedFiscalYear}
              onChange={(e) => setSelectedFiscalYear(e.target.value)}
              className="w-full md:w-96 px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all"
            >
              <option value="">-- Select a fiscal year --</option>
              {fiscalYears.map((year, index) => {
                // Handle different possible response formats
                const yearValue =
                  year.name || year.fiscalYear || year.year || year;
                const yearDisplay =
                  year.displayName ||
                  year.name ||
                  year.fiscalYear ||
                  year.year ||
                  year;
                return (
                  <option key={index} value={yearValue}>
                    {yearDisplay}
                  </option>
                );
              })}
            </select>
          ) : (
            <p className="text-gray-500">No closed fiscal years available</p>
          )}
        </div>

        <button
          onClick={handleGenerateP60}
          disabled={
            isGenerating ||
            !selectedFiscalYear ||
            isLoadingYears ||
            isLoadingProfile ||
            !employeeNo
          }
          className="bg-gradient-to-r from-blue-600 to-cyan-600 text-white px-8 py-4 rounded-xl font-semibold text-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-3"
        >
          {isGenerating ? (
            <>
              <Loader className="w-5 h-5 animate-spin" />
              <span>Generating P60...</span>
            </>
          ) : (
            <>
              <FileText className="w-5 h-5" />
              <span>Generate P60 Document</span>
            </>
          )}
        </button>
      </div>

      {/* Success Message */}
      {success && !error && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-green-900 mb-2">
                P60 Generated Successfully!
              </p>
              <p className="text-sm text-green-800 mb-4">
                Your P60 document for fiscal year {selectedFiscalYear} has been
                generated. You can view it below or download it to your device.
              </p>
              <button
                onClick={handleDownload}
                className="bg-green-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-green-700 transition-colors flex items-center gap-2"
              >
                <Download className="w-4 h-4" />
                Download P60
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 mb-6">
          <div className="flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <div>
              <p className="text-sm text-red-800">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* PDF Preview */}
      {pdfUrl && (
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Document Preview
            </h2>
            <button
              onClick={handleDownload}
              className="text-blue-600 hover:text-blue-700 font-semibold flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Download
            </button>
          </div>
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <iframe
              src={pdfUrl}
              className="w-full h-[800px]"
              title="P60 Document Preview"
            />
          </div>
        </div>
      )}

      {/* Help Section */}
      <div className="bg-gray-50 rounded-xl p-6 mt-6">
        <h3 className="font-semibold text-gray-900 mb-3">Need Help?</h3>
        <ul className="space-y-2 text-sm text-gray-600">
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">•</span>
            <span>
              If you're having trouble generating your P60, please contact HR
              support
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">•</span>
            <span>
              Keep your P60 safe - you may need it for tax claims or proof of
              earnings
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">•</span>
            <span>
              P60 documents are only available for closed fiscal years
            </span>
          </li>
          <li className="flex items-start gap-2">
            <span className="text-blue-600 font-bold">•</span>
            <span>
              If you've lost your P60, you can regenerate it here at any time
            </span>
          </li>
        </ul>
      </div>
    </div>
  );
};;

export default EmployeeP60;
